import { zodResolver } from '@hookform/resolvers/zod';
import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { z } from 'zod';

import { LAITextField } from '~/components/form';
import { createFormInvalidHandler, formatLiftAPIClientError } from '~/components/form/utils';
import { contractsEndCreate } from '~/data/generated/endpoints/contracts/contracts';
import { revalidatePropertyContracts } from '~/data/hooks/useContracts';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

type EndContractDialogProps = {
  isOpen: boolean;
  contractId: string;
  propertyId: string;
  onContractEnded: () => void;
  onCancel: () => void;
};

const endContractFormSchema = z.object({
  contractId: z.string(),
  endDate: z.string(),
});

type EndContractForm = z.infer<typeof endContractFormSchema>;

const endContractMutation = async (data: EndContractForm) => {
  await contractsEndCreate(data.contractId, { endDate: data.endDate });
};

const getDefaultDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

export const EndContractDialog = ({
  isOpen,
  contractId,
  propertyId,
  onContractEnded,
  onCancel,
}: EndContractDialogProps) => {
  const { showSnackbar } = useLAISnackbar();
  const { control, handleSubmit, reset } = useForm<EndContractForm>({
    defaultValues: {
      contractId,
      endDate: getDefaultDate(),
    },
    resolver: zodResolver(endContractFormSchema),
  });
  const { isSubmitting, errors } = useFormState({ control });

  const onSubmit = async (data: EndContractForm) => {
    try {
      const formData = endContractFormSchema.parse(data);
      await endContractMutation(formData);
    } catch (error) {
      showSnackbar(formatLiftAPIClientError('Error ending contract', error), {
        variant: 'error',
      });
      return;
    }

    void revalidatePropertyContracts(propertyId);

    onContractEnded();
  };

  useEffect(() => {
    if (contractId) {
      reset({
        contractId,
        endDate: getDefaultDate(),
      });
    }
  }, [contractId, reset]);

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="xs" fullWidth sx={{ padding: 2 }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: 1,
        }}
      >
        {/* TODO: Add contract name */}
        End Contract
        <IconButton onClick={onCancel} size="small" aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          id="endContractForm"
          onSubmit={handleSubmit(
            onSubmit,
            createFormInvalidHandler<EndContractForm>({
              formName: 'EndContractDialog',
              showSnackbar,
            }),
          )}
        >
          <FormControl fullWidth sx={{ p: 1, mt: 2 }}>
            <LAITextField
              label="Contract End Date"
              type="date"
              size="small"
              sx={{ width: '200px' }}
              name="endDate"
              control={control}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </FormControl>
        </form>
        {errors.root?.serverError ? (
          <FormHelperText error sx={{ mt: 1 }}>
            {errors.root.serverError.message}
          </FormHelperText>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          form="endContractForm"
          loading={isSubmitting}
          variant="contained"
          color="primary"
          sx={BUTTON_SX}
        >
          {isSubmitting ? 'ENDING CONTRACT' : 'END CONTRACT'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
